import { IApp, IUser, FeatureName } from "@lib/common-interfaces";
import { userStore } from "./user-store";
import { trim, replace } from "lodash";

/** Store that manages features */
export class FeaturesStore {
  /** Globally-enabled and globally-disabled feature flags. */
  private globalFeatureFlags: { [key in FeatureName]?: boolean } = {};
  public setGlobalFeatureFlags(globalFeatureFlags: FeaturesStore["globalFeatureFlags"]) {
    this.globalFeatureFlags = globalFeatureFlags || {};
  }

  /** Active features for the currentUser. */
  private userFeatureFlags: string[] = [];
  private setUserFeatureFlags(userFeatureFlags: FeaturesStore["userFeatureFlags"]): void {
    this.userFeatureFlags = userFeatureFlags || [];
  }

  /** Active features for the current app’s owner. */
  private ownerFeatureFlags: string[] = [];
  private setOwnerFeatureFlags(ownerFeatureFlags: FeaturesStore["ownerFeatureFlags"]): void {
    this.ownerFeatureFlags = ownerFeatureFlags || [];
  }

  /** Active features for the current app. */
  private appFeatureFlags: string[] = [];
  private setAppFeatureFlags(appFeatureFlags: FeaturesStore["appFeatureFlags"]): void {
    this.appFeatureFlags = appFeatureFlags || [];
  }

  /**
   * Checks if a given feature key is active.
   *
   * Checks for the featureName to be active under (in the same order) -
   * 1. Global features
   * 2. User features
   * 3. Owner features
   * 4. App features
   */
  public isActive(featureName: FeatureName): boolean {
    if (!featureName) {
      return false;
    }
    if (this.globalFeatureFlags.hasOwnProperty(featureName)) {
      return this.globalFeatureFlags[featureName];
    }
    return (
      this.userFeatureFlags.includes(featureName) ||
      this.ownerFeatureFlags.includes(featureName) ||
      this.appFeatureFlags.includes(featureName)
    );
  }

  /** Update the feature store from the given user. */
  public updateFeaturesFromUser(user: IUser): void {
    this.setUserFeatureFlags((user && user.feature_flags) || []);
  }

  /**
   * Update the feature store from the given app.
   * Only to be used by the shell to update features after an app change.
   */
  public updateFeaturesFromApp(app: IApp): void {
    this.setAppFeatureFlags((app && app.feature_flags) || []);
    this.setOwnerFeatureFlags((app && app.owner && app.owner.feature_flags) || []);
  }

  /** Gets the info on current features in the feature store. */
  public featuresInfo() {
    return {
      global: this.globalFeatureFlags,
      user: this.userFeatureFlags,
      owner: this.ownerFeatureFlags,
      app: this.appFeatureFlags
    };
  }

  /** Returns a normalized feature name given the route, os and platform. */
  public getNormalizedFeatureName(route: string, os: string, platform: string): FeatureName {
    const nameSeparator = "_";

    route = trim(replace(route || "", "/", nameSeparator)).toLowerCase();
    os = trim(os || "").toLowerCase();
    platform = trim(replace(platform || "", "-", nameSeparator)).toLowerCase();

    return [route, os, platform].join(nameSeparator) as FeatureName;
  }
}

export const featuresStore = new FeaturesStore();

// For quick information for debugging.
window["_sonoma_features"] = () => JSON.stringify(featuresStore.featuresInfo());

// Update the feature store with the globally-enabled and globally-disabled features
featuresStore.setGlobalFeatureFlags(
  window && window["initProps"] && window["initProps"]["globalFeatureFlags"] ? window["initProps"]["globalFeatureFlags"] : {}
);

// Update the feature store with the user features
featuresStore.updateFeaturesFromUser(userStore.currentUser);
